import { useState } from 'react';

// Types for farm vehicle finance calculator
export interface FarmFinanceInputs {
  vehiclePrice: number;          // Base vehicle price
  downPayment: number;           // Initial deposit
  farmEquipment: number;         // Additional farm equipment
  implementsAttachments: number; // Implements and attachments
  loanTerm: number;              // Loan term in months
  interestRate: number;          // Annual interest rate (%)
  balloonPayment: number;        // Optional balloon payment at end
  applicationFee: number;        // Loan application fee
  annualFee: number;             // Annual loan maintenance fee
  businessUsePercentage: number; // Percentage of vehicle used for farm business
  seasonalPayments: boolean;     // Whether to use seasonal payments
  primaryProducer: boolean;      // Whether the applicant is a primary producer
}

export interface AmortizationRow {
  month: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
  taxDeduction?: number;
}

export interface FarmFinanceResult {
  monthlyPayment: number;
  totalInterest: number;
  totalCost: number;
  loanAmount: number;
  estimatedTaxSavings: number;
  effectiveMonthlyCost: number;
  depreciationSchedule: DepreciationEntry[];
  amortizationSchedule: AmortizationRow[];
  seasonalPaymentSchedule?: SeasonalPayment[];
}

export interface DepreciationEntry {
  year: number;
  openingValue: number;
  depreciationAmount: number;
  closingValue: number;
  taxSaving: number;
}

export interface SeasonalPayment {
  period: string;
  months: number[];
  paymentAmount: number;
}

// Constants
const TAX_RATE = 0.30; // Average Australian farm business tax rate
const DEPRECIATION_RATE = 0.15; // Simplified diminishing value depreciation rate
const INSTANT_ASSET_WRITEOFF_THRESHOLD = 20000; // Current Australian instant asset write-off threshold

// Seasonal periods for Australian farming
export const SEASONAL_PERIODS = [
  { name: 'Harvest Period (High)', months: [11, 12, 1, 2], paymentFactor: 1.5 },
  { name: 'Planting Period (Medium)', months: [3, 4, 5, 6], paymentFactor: 1.0 },
  { name: 'Off-Season (Low)', months: [7, 8, 9, 10], paymentFactor: 0.5 }
];

// Farm vehicle types and their tax depreciation rates
export const FARM_VEHICLE_TYPES = [
  { value: 'tractor', label: 'Tractor', depreciationRate: 0.15 },
  { value: 'harvester', label: 'Harvester', depreciationRate: 0.15 },
  { value: 'utility', label: 'Farm Utility Vehicle', depreciationRate: 0.30 },
  { value: 'truck', label: 'Farm Truck', depreciationRate: 0.30 },
  { value: 'atv', label: 'ATV/Quad Bike', depreciationRate: 0.40 },
  { value: 'irrigation', label: 'Irrigation Equipment', depreciationRate: 0.15 }
];

// Calculate farm vehicle finance
export function calculateFarmFinance(inputs: FarmFinanceInputs): FarmFinanceResult {
  // Calculate total loan amount including equipment and attachments
  const loanAmount = 
    (inputs.vehiclePrice + inputs.farmEquipment + inputs.implementsAttachments - inputs.downPayment) + 
    inputs.applicationFee;
  
  // Calculate monthly interest rate
  const monthlyRate = inputs.interestRate / 100 / 12;
  
  // Adjusted loan amount accounting for balloon payment
  const adjustedLoanAmount = loanAmount - (inputs.balloonPayment / Math.pow(1 + monthlyRate, inputs.loanTerm));
  
  // Calculate regular monthly payment
  let monthlyPayment = 0;
  if (monthlyRate === 0) {
    monthlyPayment = adjustedLoanAmount / inputs.loanTerm;
  } else {
    monthlyPayment = adjustedLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, inputs.loanTerm) 
                     / (Math.pow(1 + monthlyRate, inputs.loanTerm) - 1);
  }
  
  // Calculate amortization schedule
  let amortizationSchedule = generateAmortizationSchedule(
    loanAmount,
    monthlyRate,
    inputs.loanTerm,
    monthlyPayment,
    inputs.balloonPayment,
    inputs.businessUsePercentage
  );
  
  // Calculate total interest
  const totalInterest = amortizationSchedule.reduce((sum, row) => sum + row.interest, 0);
  
  // Calculate total cost
  const totalCost = loanAmount + totalInterest;
  
  // Calculate tax deductions and savings
  const businessPortion = inputs.businessUsePercentage / 100;
  const taxDeductibleInterest = totalInterest * businessPortion;
  
  // Calculate depreciation schedule
  const assetValue = (inputs.vehiclePrice + inputs.farmEquipment + inputs.implementsAttachments) * businessPortion;
  const depreciationSchedule = generateDepreciationSchedule(
    assetValue,
    DEPRECIATION_RATE,
    Math.ceil(inputs.loanTerm / 12),
    inputs.primaryProducer && assetValue <= INSTANT_ASSET_WRITEOFF_THRESHOLD
  );
  
  // Calculate estimated tax savings
  const interestTaxSaving = taxDeductibleInterest * TAX_RATE;
  const depreciationTaxSaving = depreciationSchedule.reduce((sum, entry) => sum + entry.taxSaving, 0);
  const estimatedTaxSavings = interestTaxSaving + depreciationTaxSaving;
  
  // Calculate effective monthly cost after tax savings
  const effectiveMonthlyCost = monthlyPayment - (estimatedTaxSavings / inputs.loanTerm);
  
  // Generate seasonal payment schedule if selected
  let seasonalPaymentSchedule;
  if (inputs.seasonalPayments) {
    seasonalPaymentSchedule = generateSeasonalPaymentSchedule(
      monthlyPayment,
      inputs.loanTerm
    );
    
    // Adjust amortization schedule for seasonal payments
    if (seasonalPaymentSchedule) {
      amortizationSchedule = adjustAmortizationForSeasonalPayments(
        loanAmount,
        monthlyRate,
        inputs.loanTerm,
        seasonalPaymentSchedule,
        inputs.balloonPayment,
        inputs.businessUsePercentage
      );
    }
  }
  
  return {
    monthlyPayment,
    totalInterest,
    totalCost,
    loanAmount,
    estimatedTaxSavings,
    effectiveMonthlyCost,
    depreciationSchedule,
    amortizationSchedule,
    seasonalPaymentSchedule
  };
}

// Generate amortization schedule
function generateAmortizationSchedule(
  principal: number,
  monthlyRate: number,
  totalPeriods: number,
  monthlyPayment: number,
  balloonPayment: number,
  businessUsePercentage: number
): AmortizationRow[] {
  let balance = principal;
  const schedule: AmortizationRow[] = [];
  
  for (let month = 1; month <= totalPeriods; month++) {
    // Handle last payment with balloon
    if (month === totalPeriods && balloonPayment > 0) {
      const interest = balance * monthlyRate;
      const principalPayment = balance - balloonPayment;
      balance = balloonPayment;
      
      const taxDeduction = interest * (businessUsePercentage / 100);
      
      schedule.push({
        month,
        payment: principalPayment + interest,
        principal: principalPayment,
        interest,
        balance,
        taxDeduction
      });
      
      // Add balloon payment as final payment
      schedule.push({
        month: month + 1,
        payment: balloonPayment,
        principal: balloonPayment,
        interest: 0,
        balance: 0,
        taxDeduction: 0
      });
      
      break;
    }
    
    const interest = balance * monthlyRate;
    let principalPayment = monthlyPayment - interest;
    
    // Adjust for final payment to ensure balance reaches zero
    if (month === totalPeriods) {
      principalPayment = balance;
    }
    
    balance -= principalPayment;
    
    const taxDeduction = interest * (businessUsePercentage / 100);
    
    schedule.push({
      month,
      payment: principalPayment + interest,
      principal: principalPayment,
      interest,
      balance: Math.max(0, balance),
      taxDeduction
    });
  }
  
  return schedule;
}

// Generate depreciation schedule
function generateDepreciationSchedule(
  assetValue: number,
  depreciationRate: number,
  years: number,
  instantAssetWriteOff: boolean
): DepreciationEntry[] {
  const schedule: DepreciationEntry[] = [];
  
  if (instantAssetWriteOff) {
    // Instant asset write-off - full deduction in first year
    schedule.push({
      year: 1,
      openingValue: assetValue,
      depreciationAmount: assetValue,
      closingValue: 0,
      taxSaving: assetValue * TAX_RATE
    });
    
    // Add remaining years with zero depreciation
    for (let year = 2; year <= years; year++) {
      schedule.push({
        year,
        openingValue: 0,
        depreciationAmount: 0,
        closingValue: 0,
        taxSaving: 0
      });
    }
  } else {
    // Normal diminishing value depreciation
    let currentValue = assetValue;
    
    for (let year = 1; year <= years; year++) {
      const openingValue = currentValue;
      const depreciationAmount = openingValue * depreciationRate;
      currentValue -= depreciationAmount;
      
      schedule.push({
        year,
        openingValue,
        depreciationAmount,
        closingValue: currentValue,
        taxSaving: depreciationAmount * TAX_RATE
      });
    }
  }
  
  return schedule;
}

// Generate seasonal payment schedule
function generateSeasonalPaymentSchedule(
  baseMonthlyPayment: number,
  totalPeriods: number
): SeasonalPayment[] {
  const schedule: SeasonalPayment[] = [];
  const yearlyPayment = baseMonthlyPayment * 12;
  
  // Calculate adjusted payment amounts based on seasonal factors
  for (const period of SEASONAL_PERIODS) {
    const monthsInPeriod = period.months.length;
    const paymentShare = (period.paymentFactor * monthsInPeriod) / 
                         SEASONAL_PERIODS.reduce((sum, p) => sum + (p.paymentFactor * p.months.length), 0);
    
    const paymentAmount = (yearlyPayment * paymentShare) / monthsInPeriod;
    
    schedule.push({
      period: period.name,
      months: period.months,
      paymentAmount
    });
  }
  
  return schedule;
}

// Adjust amortization schedule for seasonal payments
function adjustAmortizationForSeasonalPayments(
  principal: number,
  monthlyRate: number,
  totalPeriods: number,
  seasonalPayments: SeasonalPayment[],
  balloonPayment: number,
  businessUsePercentage: number
): AmortizationRow[] {
  let balance = principal;
  const schedule: AmortizationRow[] = [];
  
  for (let month = 1; month <= totalPeriods; month++) {
    // Determine which seasonal period this month falls into
    const calendarMonth = ((month - 1) % 12) + 1;
    const seasonalPeriod = seasonalPayments.find(period => 
      period.months.includes(calendarMonth)
    );
    
    const payment = seasonalPeriod ? seasonalPeriod.paymentAmount : 0;
    
    // Handle last payment with balloon
    if (month === totalPeriods && balloonPayment > 0) {
      const interest = balance * monthlyRate;
      const principalPayment = balance - balloonPayment;
      balance = balloonPayment;
      
      const taxDeduction = interest * (businessUsePercentage / 100);
      
      schedule.push({
        month,
        payment: principalPayment + interest,
        principal: principalPayment,
        interest,
        balance,
        taxDeduction
      });
      
      // Add balloon payment as final payment
      schedule.push({
        month: month + 1,
        payment: balloonPayment,
        principal: balloonPayment,
        interest: 0,
        balance: 0,
        taxDeduction: 0
      });
      
      break;
    }
    
    const interest = balance * monthlyRate;
    let principalPayment = payment - interest;
    
    // Ensure principal payment is positive
    principalPayment = Math.max(0, principalPayment);
    
    // Adjust for final payment to ensure balance reaches zero
    if (month === totalPeriods) {
      principalPayment = balance;
    }
    
    balance -= principalPayment;
    
    const taxDeduction = interest * (businessUsePercentage / 100);
    
    schedule.push({
      month,
      payment,
      principal: principalPayment,
      interest,
      balance: Math.max(0, balance),
      taxDeduction
    });
  }
  
  return schedule;
}

// Format currency for display
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', { 
    style: 'currency', 
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }).format(value);
}

// Format percentage for display
export function formatPercentage(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
}

// Calculate the annual tax benefit from interest and depreciation
export function calculateAnnualTaxBenefit(
  interestPaid: number,
  depreciationAmount: number,
  businessUsePercentage: number,
  taxRate: number = TAX_RATE
): number {
  const deductibleInterest = interestPaid * (businessUsePercentage / 100);
  const taxableDeduction = deductibleInterest + depreciationAmount;
  return taxableDeduction * taxRate;
}

// Check eligibility for instant asset write-off
export function checkInstantAssetWriteOffEligibility(
  assetValue: number,
  isPrimaryProducer: boolean,
  threshold: number = INSTANT_ASSET_WRITEOFF_THRESHOLD
): boolean {
  return isPrimaryProducer && assetValue <= threshold;
}

// Calculate effective interest rate after tax benefits
export function calculateEffectiveRate(
  interestRate: number,
  businessUsePercentage: number,
  taxRate: number = TAX_RATE
): number {
  const taxDeductionFactor = (businessUsePercentage / 100) * taxRate;
  return interestRate * (1 - taxDeductionFactor);
} 